import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  Grid,
  useToast,
  useColorModeValue,
  IconButton,
  Spinner,
  Center,
  Badge
} from '@chakra-ui/react';
import { FiHeart, FiShare2, FiPlay } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useAnime } from '../contexts/UnifiedContentContext';
import { GENRES } from '../constants/genres';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';

const Anime: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const {
    anime,
    loading,
    error,
    selectedGenre,
    userLikes,
    likeCounts,
    likingAnime,
    loadAnime,
    setSelectedGenre,
    handleLike,
    handleShare
  } = useAnime();

  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const titleColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const genres = [
    { id: 'all', name: 'All Genres' },
    ...GENRES.map(genre => ({ id: genre.toLowerCase(), name: genre }))
  ];

  useEffect(() => {
    loadAnime(user?.uid);
  }, [selectedGenre, user?.uid, loadAnime]);

  const handleGenreChange = (genre: string) => {
    setSelectedGenre(genre);
  };

  const handleAnimeClick = (animeId: string) => {
    navigate(`/content/${animeId}`);
  };

  const handleLikeClick = async (animeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to like content',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await handleLike(animeId, user.uid);
      const currentLike = userLikes[animeId];
      const newLikeType = currentLike === 'like' ? 'dislike' : 'like';
      
      toast({
        title: currentLike === 'like' ? 'Like removed' : 'Liked!',
        description: currentLike === 'like' ? 'You removed your like' : `You ${newLikeType}d this anime`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update like. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleShareClick = async (animeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      await handleShare(animeId, user?.uid);
      toast({
        title: 'Link copied!',
        description: 'Share link copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to share. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Center h="50vh">
        <Spinner size="xl" color="purple.500" />
      </Center>
    );
  }

  if (error || anime.length === 0) {
    return (
      <Center h="50vh">
        <VStack gap={6} textAlign="center" maxW="400px">
          <Text fontSize="2xl" fontWeight="bold" color={titleColor}>
            Content Not Found
          </Text>
          <Text fontSize="md" color={labelColor}>
            Content not found or no longer available
          </Text>
          <VStack gap={3}>
            <Button onClick={() => loadAnime(user?.uid)} colorScheme="purple" size="lg">
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/upload')} 
              variant="outline" 
              colorScheme="purple"
            >
              Upload Content
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" px={4} py={6}>
      <VStack gap={6} maxW="1400px" mx="auto">
        {/* Header */}
        <HStack w="full" justify="space-between">
          <Text fontSize="2xl" fontWeight="bold" color={titleColor}>
            Anime
          </Text>
          <Select
            value={selectedGenre}
            onChange={(e) => handleGenreChange(e.target.value)}
            w="200px"
            bg={cardBg}
          >
            {genres.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </Select>
        </HStack>

        {/* Anime Grid */}
        <Grid
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)'
          }}
          gap={6}
          w="full"
        >
          {anime.map((animeItem) => (
            <Box
              key={animeItem.id}
              bg={cardBg}
              borderRadius="lg"
              overflow="hidden"
              cursor="pointer"
              onClick={() => handleAnimeClick(animeItem.id)}
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
              transition="all 0.2s"
            >
              {/* Thumbnail */}
              <Box position="relative" h="300px">
                <CachedImage
                  src={animeItem.thumbnail}
                  alt={animeItem.title}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
                <Badge
                  position="absolute"
                  top={2}
                  right={2}
                  colorScheme="orange"
                  variant="solid"
                >
                  {animeItem.rating}
                </Badge>
              </Box>

              {/* Content */}
              <VStack p={4} align="start" gap={3}>
                <VStack align="start" gap={1} w="full">
                  <Text
                    fontSize="md"
                    fontWeight="bold"
                    color={textColor}
                    noOfLines={2}
                  >
                    {animeItem.title}
                  </Text>
                  <Text fontSize="sm" color="gray.500" noOfLines={2}>
                    {animeItem.description}
                  </Text>
                </VStack>

                {/* Action Buttons */}
                <HStack w="full" justify="space-between">
                  <HStack gap={2}>
                    <IconButton
                      aria-label="Like"
                      icon={<IconWrapper icon={FiHeart} size={16} />}
                      size="sm"
                      variant={userLikes[animeItem.id] === 'like' ? 'solid' : 'ghost'}
                      colorScheme="red"
                      onClick={(e) => handleLikeClick(animeItem.id, e)}
                      isDisabled={!user || likingAnime.has(animeItem.id)}
                      isLoading={likingAnime.has(animeItem.id)}
                    />
                    <Text fontSize="xs" color="white">
                      {likeCounts[animeItem.id]?.likes || animeItem.likes}
                    </Text>
                  </HStack>

                  <IconButton
                    aria-label="Share"
                    icon={<IconWrapper icon={FiShare2} size={16} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={(e) => handleShareClick(animeItem.id, e)}
                  />

                  <Button
                    size="sm"
                    colorScheme="purple"
                    leftIcon={<IconWrapper icon={FiPlay} size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnimeClick(animeItem.id);
                    }}
                  >
                    Watch
                  </Button>
                </HStack>
              </VStack>
            </Box>
          ))}
        </Grid>
      </VStack>
    </Box>
  );
};

export default Anime;
